import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, 
  List, 
  CheckCircle2, 
  Clock, 
  ArrowRight,
  Sparkles,
  FileText,
  Settings,
  TrendingUp,
  Calendar,
  PauseCircle,
  Ban,
  X,
  PartyPopper,
  FolderOpen,
  Send
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalTasks: 0,
    pendingTasks: 0,
    inProgressTasks: 0,
    holdTasks: 0,
    completedTasks: 0,
    defunctTasks: 0,
    recentTasks: []
  });
  const [loading, setLoading] = useState(true);
  const [completionNudges, setCompletionNudges] = useState([]);
  const [dismissedNudges, setDismissedNudges] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem('dismissedCompletionNudges') || '[]')); }
    catch { return new Set(); }
  });
  // Nudge auto-collapse — once the banner has been visible for a few seconds
  // (or the user has already opened the dashboard this session) it shrinks
  // into a slim pill you can tap to re-expand. Keeps the top of the page
  // clean without hiding the update entirely.
  const [nudgeCollapsed, setNudgeCollapsed] = useState(() => {
    try { return sessionStorage.getItem('nudgeCollapsed') === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    if (nudgeCollapsed) return;
    const t = setTimeout(() => {
      setNudgeCollapsed(true);
      try { sessionStorage.setItem('nudgeCollapsed', '1'); } catch { /* ignore */ }
    }, 6000);
    return () => clearTimeout(t);
  }, [nudgeCollapsed]);

  // Dynamic time-of-day greeting + rotating engagement tagline. The tagline
  // rotates deterministically per day so the same message is stable
  // through a session but feels fresh across visits.
  const greeting = React.useMemo(() => {
    const h = new Date().getHours();
    if (h < 5) return 'Working late';
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    if (h < 22) return 'Good evening';
    return 'Winding down';
  }, []);
  const fullGreeting = `${greeting}, ${user?.name?.split(' ')[0] || 'there'}`;
  const tagline = React.useMemo(() => {
    const taglines = [
      'Ready to ship something great today?',
      'Every brief starts as a spark — let’s catch it.',
      'A clear brief is half the delivery.',
      'Small edits, big impact — you’ve got this.',
      'The best campaigns start with the best briefs.',
      'Momentum > motivation. Post your next task.',
      'Design your day. Ship the details.',
    ];
    const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
    return taglines[dayOfYear % taglines.length];
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetch(`${API_URL}/api/init/seed-data`, {
          method: 'POST',
          credentials: 'include'
        });

        const [tasksRes, notifRes] = await Promise.all([
          fetch(`${API_URL}/api/tasks`, { credentials: 'include' }),
          fetch(`${API_URL}/api/notifications?limit=20&unread_only=true`, { credentials: 'include' }),
        ]);

        if (tasksRes.ok) {
          const tasks = await tasksRes.json();
          setStats({
            totalTasks: tasks.length,
            pendingTasks: tasks.filter(t => t.status === 'pending').length,
            inProgressTasks: tasks.filter(t => t.status === 'in_progress').length,
            holdTasks: tasks.filter(t => t.status === 'hold').length,
            completedTasks: tasks.filter(t => t.status === 'completed').length,
            defunctTasks: tasks.filter(t => t.status === 'defunct').length,
            recentTasks: tasks.slice(0, 5)
          });
        }

        if (notifRes.ok) {
          const notifs = await notifRes.json();
          const completed = (Array.isArray(notifs) ? notifs : []).filter(n => n.type === 'task_completed');
          setCompletionNudges(completed);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const dismissNudge = (id) => {
    setDismissedNudges((prev) => {
      const next = new Set(prev);
      next.add(id);
      try { sessionStorage.setItem('dismissedCompletionNudges', JSON.stringify([...next])); } catch { /* ignore quota errors */ }
      return next;
    });
  };

  const openTask = async (n) => {
    // Mark as read in the background
    try {
      await fetch(`${API_URL}/api/notifications/${n.id}/mark-read`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      /* non-blocking */
    }
    if (n.task_display_id || n.task_id) {
      navigate(`/tasks?taskId=${encodeURIComponent(n.task_display_id || n.task_id)}`);
    } else {
      navigate('/tasks');
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
      in_progress: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: TrendingUp },
      completed: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 }
    };
    return configs[status] || configs.pending;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <div className="w-12 h-12 rounded-full border-4 border-[#2EB4AC]/20 border-t-[#2EB4AC] animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Completion nudge — auto-collapses into a slim pill after ~6s so the
          top of the page stays clean. Tap the pill to re-expand. */}
      {completionNudges.filter(n => !dismissedNudges.has(n.id)).length > 0 && (
        nudgeCollapsed ? (
          <button
            type="button"
            onClick={() => {
              setNudgeCollapsed(false);
              try { sessionStorage.removeItem('nudgeCollapsed'); } catch { /* ignore */ }
            }}
            className="group inline-flex items-center gap-2 h-9 px-3 pr-4 rounded-full border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 shadow-sm transition-all hover:-translate-y-0.5"
            data-testid="completion-nudge-pill"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            <PartyPopper className="h-3.5 w-3.5 text-emerald-700" />
            <span className="text-xs font-semibold text-emerald-900">
              {completionNudges.filter(n => !dismissedNudges.has(n.id)).length} completed
            </span>
            <span className="text-[10px] font-medium text-emerald-700/70 group-hover:text-emerald-800">Tap to view</span>
          </button>
        ) : (
        <div
          className="relative overflow-hidden rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-teal-50/60 p-4 md:p-5 animate-fade-in"
          data-testid="completion-nudge-banner"
        >
          <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-emerald-200/40 blur-3xl pointer-events-none" />
          <div className="relative flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white flex items-center justify-center shadow-lg shadow-emerald-500/20 flex-shrink-0">
              <PartyPopper className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm md:text-base font-bold text-emerald-900">
                  {completionNudges.filter(n => !dismissedNudges.has(n.id)).length === 1
                    ? '1 task you submitted has been completed'
                    : `${completionNudges.filter(n => !dismissedNudges.has(n.id)).length} tasks you submitted have been completed`}
                </h3>
                <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-full bg-emerald-500 text-white">
                  New
                </span>
              </div>
              <p className="text-xs text-emerald-800/80 mt-0.5">
                Review the deliverables and raise a Rework Request from the Task Log if anything needs changes.
              </p>
              <div className="mt-2.5 flex flex-wrap gap-2">
                {completionNudges
                  .filter(n => !dismissedNudges.has(n.id))
                  .slice(0, 5)
                  .map(n => (
                    <button
                      type="button"
                      key={n.id}
                      onClick={() => openTask(n)}
                      className="group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-300/70 bg-white text-emerald-800 text-[11px] font-semibold hover:bg-emerald-100 hover:border-emerald-400 transition-colors shadow-sm"
                      data-testid={`completion-nudge-${n.task_display_id || n.id}`}
                    >
                      <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                      {n.task_display_id || 'Open'}
                      <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 -ml-1 group-hover:ml-0 transition-all" />
                    </button>
                  ))}
                {completionNudges.filter(n => !dismissedNudges.has(n.id)).length > 5 && (
                  <span className="inline-flex items-center px-2 py-1.5 text-[11px] text-emerald-700">
                    +{completionNudges.filter(n => !dismissedNudges.has(n.id)).length - 5} more
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setNudgeCollapsed(true);
                try { sessionStorage.setItem('nudgeCollapsed', '1'); } catch { /* ignore */ }
              }}
              className="w-7 h-7 rounded-lg text-emerald-700 hover:bg-emerald-100 flex items-center justify-center flex-shrink-0"
              aria-label="Collapse"
              data-testid="dismiss-completion-nudge"
              title="Collapse"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        )
      )}

      {/* Hero — split layout inspired by Claude's home screen.
          LEFT: editorial gradient panel with signature glyph + greeting.
          RIGHT: a stylised "prompt bar" launcher and workflow suggestion
          chips that deep-link into the app's core surfaces. */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#2EB4AC] via-[#1F8F88] to-[#0F5E5A] p-1.5">
        {/* Ambient background flourishes — soft glows + subtle dot grid */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle at 12% 20%, rgba(255,255,255,0.18) 0%, transparent 45%),
                            radial-gradient(circle at 88% 85%, rgba(15,94,90,0.55) 0%, transparent 55%)`
        }} />
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cg fill='%23fff'%3E%3Ccircle cx='30' cy='30' r='0.7'/%3E%3C/g%3E%3C/svg%3E")`,
        }} />
        {/* Floating aura dot — slow float for depth */}
        <div className="absolute -top-16 -left-16 w-56 h-56 rounded-full bg-white/10 blur-3xl pointer-events-none animate-float" />

        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch px-5 md:px-7 py-6 md:py-8">
          {/* LEFT: editorial greeting */}
          <div className="lg:col-span-7 flex flex-col justify-center animate-fade-in-up">
            {/* Signature sunburst glyph — Claude-esque signature mark */}
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-white/12 backdrop-blur-md border border-white/25 shadow-lg shadow-black/10">
                <span className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/30 to-transparent" />
                <Sparkles className="relative h-4 w-4 text-white" />
              </span>
              <span className="text-white/70 text-[11px] font-semibold tracking-[0.18em] uppercase">
                Prism • {new Date().toLocaleDateString(undefined, { weekday: 'long' })}
              </span>
            </div>

            <h1
              className="text-white text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] animate-fade-in-up"
              style={{ animationDelay: '80ms' }}
              data-testid="hero-greeting"
            >
              {fullGreeting}
              <span className="text-white/50 font-bold">.</span>
            </h1>

            <p
              className="mt-4 text-white/80 text-sm md:text-base max-w-xl leading-relaxed animate-fade-in-up"
              style={{ animationDelay: '160ms' }}
            >
              {tagline}
            </p>

            {/* Micro stat line — grounded, minimal, no chip clutter */}
            <div
              className="mt-6 flex items-center gap-4 text-[11px] font-medium text-white/70 animate-fade-in-up"
              style={{ animationDelay: '240ms' }}
            >
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-white/80" />
                {stats.totalTasks} total tasks
              </span>
              <span className="w-px h-3 bg-white/25" />
              <span className="inline-flex items-center gap-1.5">
                <TrendingUp className="h-3 w-3" />
                {stats.completedTasks} completed
              </span>
              {stats.pendingTasks + stats.inProgressTasks > 0 && (
                <>
                  <span className="w-px h-3 bg-white/25" />
                  <span className="inline-flex items-center gap-1.5">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white/70 opacity-75" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                    </span>
                    {stats.pendingTasks + stats.inProgressTasks} open
                  </span>
                </>
              )}
            </div>
          </div>

          {/* RIGHT: Claude-style prompt bar launcher + workflow chips */}
          <div className="lg:col-span-5 flex flex-col justify-center gap-3">
            <Link
              to="/submit"
              className="group relative block rounded-2xl bg-white/12 hover:bg-white/16 backdrop-blur-xl border border-white/25 hover:border-white/40 shadow-2xl shadow-black/20 transition-all hover:-translate-y-0.5 animate-fade-in-up"
              style={{ animationDelay: '120ms' }}
              data-testid="hero-prompt-launcher"
              aria-label="Submit a new task"
            >
              {/* soft inner highlight */}
              <span className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/20 via-transparent to-transparent pointer-events-none" />
              <div className="relative flex items-center gap-3 px-4 py-4">
                <span className="w-9 h-9 rounded-xl bg-white/20 border border-white/30 flex items-center justify-center flex-shrink-0 group-hover:bg-white/30 group-hover:rotate-6 transition-all">
                  <Plus className="h-4 w-4 text-white" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-white/60">
                    Start a brief
                  </p>
                  <p className="text-white/95 text-sm md:text-base font-medium mt-0.5 truncate">
                    What are you shipping today?
                  </p>
                </div>
                <span className="w-9 h-9 rounded-xl bg-white text-[#0F5E5A] flex items-center justify-center flex-shrink-0 shadow-md group-hover:shadow-lg group-hover:scale-105 transition-all">
                  <Send className="h-4 w-4" />
                </span>
              </div>
            </Link>

            {/* Workflow suggestion chips */}
            <div
              className="flex flex-wrap items-center gap-2 animate-fade-in-up"
              style={{ animationDelay: '200ms' }}
            >
              <Link
                to="/submit"
                className="group inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white/12 hover:bg-white/22 backdrop-blur-md border border-white/25 text-white text-[12px] font-medium transition-all hover:-translate-y-0.5"
                data-testid="hero-chip-new-task"
              >
                <Plus className="h-3 w-3" />
                New Task
              </Link>
              <Link
                to="/tasks"
                className="group inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white text-[12px] font-medium transition-all hover:-translate-y-0.5"
                data-testid="hero-chip-task-log"
              >
                <List className="h-3 w-3" />
                View Task Log
              </Link>
              <Link
                to="/brand-workspace"
                className="group inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white text-[12px] font-medium transition-all hover:-translate-y-0.5"
                data-testid="hero-chip-brand-workspace"
              >
                <FolderOpen className="h-3 w-3" />
                Brand Workspace
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid — reference "cards with big number + growth chip" style */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Overview card (large, spans 2) — visualises task-by-status as bars */}
        <div className="lg:col-span-2 relative overflow-hidden rounded-2xl bg-white border border-slate-100 shadow-[0_2px_8px_rgba(15,23,42,0.05)] p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-500 font-medium">Overview</p>
              <p className="mt-1 text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
                {stats.totalTasks}
                <span className="text-slate-400 text-xl">.total</span>
              </p>
            </div>
            <div className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-100">
              This month
            </div>
          </div>

          {/* Mini bar chart — one bar per non-zero status */}
          {(() => {
            const buckets = [
              { key: 'pending',      label: 'Pending',     value: stats.pendingTasks,     color: '#F59E0B' },
              { key: 'in_progress',  label: 'In Progress', value: stats.inProgressTasks,  color: '#3B82F6' },
              { key: 'hold',         label: 'Hold',        value: stats.holdTasks,        color: '#F97316' },
              { key: 'completed',    label: 'Completed',   value: stats.completedTasks,   color: '#10B981' },
              { key: 'defunct',      label: 'Defunct',     value: stats.defunctTasks,     color: '#EF4444' },
            ];
            const max = Math.max(1, ...buckets.map(b => b.value));
            return (
              <div className="mt-6">
                <div className="flex items-end gap-3 h-24">
                  {buckets.map(b => (
                    <div key={b.key} className="flex-1 flex flex-col items-center justify-end gap-1.5">
                      <div className="w-full flex flex-col items-center gap-0.5">
                        <span className="text-[10px] font-semibold text-slate-500">{b.value}</span>
                        <div
                          className="w-full rounded-t-md transition-all duration-500"
                          style={{
                            height: `${Math.max(4, (b.value / max) * 72)}px`,
                            backgroundColor: b.color,
                            opacity: b.value > 0 ? 0.9 : 0.15,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex items-end gap-3">
                  {buckets.map(b => (
                    <div key={b.key} className="flex-1 text-center">
                      <p className="text-[10px] text-slate-400 truncate">{b.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Pending */}
        <Link to="/tasks?status=pending" className="block group" data-testid="stat-card-pending">
          <div className="relative overflow-hidden rounded-2xl bg-white border border-slate-100 shadow-[0_2px_8px_rgba(15,23,42,0.05)] p-5 hover:shadow-lg hover:-translate-y-0.5 hover:border-amber-200 transition-all cursor-pointer h-full">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-600 font-medium">Pending</p>
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                <Clock className="h-4 w-4 text-amber-500" />
              </div>
            </div>
            <p className="text-3xl font-bold text-slate-900 tracking-tight">{stats.pendingTasks}</p>
            <p className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1">
              <TrendingUp className="h-3 w-3 text-amber-500" />
              awaiting kickoff
              <ArrowRight className="h-3 w-3 ml-auto opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all text-amber-500" />
            </p>
          </div>
        </Link>

        {/* In Progress */}
        <Link to="/tasks?status=in_progress" className="block group" data-testid="stat-card-in-progress">
          <div className="relative overflow-hidden rounded-2xl bg-white border border-slate-100 shadow-[0_2px_8px_rgba(15,23,42,0.05)] p-5 hover:shadow-lg hover:-translate-y-0.5 hover:border-blue-200 transition-all cursor-pointer h-full">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-600 font-medium">In Progress</p>
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-blue-500" />
              </div>
            </div>
            <p className="text-3xl font-bold text-slate-900 tracking-tight">{stats.inProgressTasks}</p>
            <p className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
              active right now
              <ArrowRight className="h-3 w-3 ml-auto opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all text-blue-500" />
            </p>
          </div>
        </Link>

        {/* Completed */}
        <Link to="/tasks?status=completed" className="block group" data-testid="stat-card-completed">
          <div className="relative overflow-hidden rounded-2xl bg-white border border-slate-100 shadow-[0_2px_8px_rgba(15,23,42,0.05)] p-5 hover:shadow-lg hover:-translate-y-0.5 hover:border-emerald-200 transition-all cursor-pointer h-full">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-600 font-medium">Completed</p>
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              </div>
            </div>
            <p className="text-3xl font-bold text-slate-900 tracking-tight">{stats.completedTasks}</p>
            <p className="text-[11px] text-slate-500 mt-1.5 flex items-center gap-1">
              <TrendingUp className="h-3 w-3 text-emerald-500" />
              {stats.totalTasks > 0 ? `${Math.round((stats.completedTasks / stats.totalTasks) * 100)}%` : '0%'} of total
              <ArrowRight className="h-3 w-3 ml-auto opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all text-emerald-500" />
            </p>
          </div>
        </Link>
      </div>

      {/* Secondary row: Hold + Defunct + Open workload + Completion rate — clickable */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link to="/tasks?status=hold" className="block group" data-testid="stat-card-hold">
          <div className="rounded-2xl bg-white border border-slate-100 shadow-[0_2px_8px_rgba(15,23,42,0.05)] p-4 flex items-center gap-3 hover:shadow-lg hover:-translate-y-0.5 hover:border-orange-200 transition-all cursor-pointer h-full">
            <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center">
              <PauseCircle className="h-4 w-4 text-orange-500" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-2xl font-bold text-slate-900" data-testid="dashboard-hold-count">{stats.holdTasks}</p>
              <p className="text-xs text-slate-500">Hold</p>
            </div>
            <ArrowRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-orange-500 group-hover:translate-x-0.5 transition-all" />
          </div>
        </Link>
        <Link to="/tasks?status=defunct" className="block group" data-testid="stat-card-defunct">
          <div className="rounded-2xl bg-white border border-slate-100 shadow-[0_2px_8px_rgba(15,23,42,0.05)] p-4 flex items-center gap-3 hover:shadow-lg hover:-translate-y-0.5 hover:border-red-200 transition-all cursor-pointer h-full">
            <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center">
              <Ban className="h-4 w-4 text-red-500" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-2xl font-bold text-slate-900" data-testid="dashboard-defunct-count">{stats.defunctTasks}</p>
              <p className="text-xs text-slate-500">Defunct</p>
            </div>
            <ArrowRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-red-500 group-hover:translate-x-0.5 transition-all" />
          </div>
        </Link>
        <Link to="/tasks?status=open" className="block group" data-testid="stat-card-open">
          <div className="rounded-2xl bg-white border border-slate-100 shadow-[0_2px_8px_rgba(15,23,42,0.05)] p-4 flex items-center gap-3 hover:shadow-lg hover:-translate-y-0.5 hover:border-[#2EB4AC]/30 transition-all cursor-pointer h-full">
            <div className="w-9 h-9 rounded-xl bg-[#2EB4AC]/10 flex items-center justify-center">
              <Calendar className="h-4 w-4 text-[#2EB4AC]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-2xl font-bold text-slate-900">
                {stats.pendingTasks + stats.inProgressTasks + stats.holdTasks}
              </p>
              <p className="text-xs text-slate-500">Open workload</p>
            </div>
            <ArrowRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-[#2EB4AC] group-hover:translate-x-0.5 transition-all" />
          </div>
        </Link>
        <Link to="/tasks" className="block group" data-testid="stat-card-completion-rate">
          <div className="rounded-2xl bg-white border border-slate-100 shadow-[0_2px_8px_rgba(15,23,42,0.05)] p-4 flex items-center gap-3 hover:shadow-lg hover:-translate-y-0.5 hover:border-violet-200 transition-all cursor-pointer h-full">
            <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-violet-500" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-2xl font-bold text-slate-900">
                {stats.totalTasks > 0 ? `${Math.round((stats.completedTasks / stats.totalTasks) * 100)}%` : '0%'}
              </p>
              <p className="text-xs text-slate-500">Completion rate</p>
            </div>
            <ArrowRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-violet-500 group-hover:translate-x-0.5 transition-all" />
          </div>
        </Link>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Tasks - Takes 2 columns */}
        <Card className="lg:col-span-2 border-0 shadow-sm bg-white overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Recent Tasks</h2>
                <p className="text-sm text-slate-500 mt-1">Your latest design requests</p>
              </div>
              <Link to="/tasks">
                <Button variant="ghost" className="text-[#2EB4AC] hover:text-[#25908A] hover:bg-[#2EB4AC]/10">
                  View all
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
          <CardContent className="p-0">
            {stats.recentTasks.length === 0 ? (
              <div className="text-center py-12 px-6">
                <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <FileText className="h-8 w-8 text-slate-400" />
                </div>
                <p className="text-slate-600 font-medium">No tasks yet</p>
                <p className="text-slate-400 text-sm mt-1">Submit your first design request</p>
                <Link to="/submit">
                  <Button className="mt-4 bg-[#2EB4AC] hover:bg-[#25908A] text-white">
                    <Plus className="mr-2 h-4 w-4" />
                    Create Task
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {stats.recentTasks.map((task, idx) => {
                  const statusConfig = getStatusConfig(task.status);
                  const StatusIcon = statusConfig.icon;
                  return (
                    <Link 
                      key={task.id} 
                      to="/tasks"
                      className="flex items-center justify-between p-4 hover:bg-slate-50/80 transition-colors group"
                      style={{ animationDelay: `${idx * 50}ms` }}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2EB4AC]/10 to-[#2EB4AC]/5 flex items-center justify-center flex-shrink-0 group-hover:from-[#2EB4AC]/20 group-hover:to-[#2EB4AC]/10 transition-colors">
                          <FileText className="h-5 w-5 text-[#2EB4AC]" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-slate-900 text-sm">{task.task_id}</p>
                            <Badge variant="secondary" className={`text-[10px] border ${statusConfig.color}`}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {task.status.replace('_', ' ')}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500 truncate mt-0.5">
                            {task.brand_name} • {task.task_type}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                        <div className="text-right hidden sm:block">
                          <p className="text-xs text-slate-400">Due</p>
                          <p className="text-sm text-slate-600">{new Date(task.required_by_date).toLocaleDateString()}</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-[#2EB4AC] group-hover:translate-x-1 transition-all" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-0 shadow-sm bg-white">
          <div className="p-6 border-b border-slate-100">
            <h2 className="text-xl font-semibold text-slate-900">Quick Actions</h2>
            <p className="text-sm text-slate-500 mt-1">Shortcuts to common tasks</p>
          </div>
          <CardContent className="p-4 space-y-3">
            <Link to="/submit" className="block">
              <div className="group relative overflow-hidden p-4 rounded-xl bg-gradient-to-r from-[#2EB4AC] to-[#20A39E] text-white hover:shadow-lg hover:shadow-[#2EB4AC]/25 transition-all hover:-translate-y-0.5">
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                    <Plus className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-semibold">New Task</p>
                    <p className="text-sm text-white/70">Submit a request</p>
                  </div>
                </div>
              </div>
            </Link>

            <Link to="/tasks" className="block">
              <div className="group p-4 rounded-xl border border-slate-200 hover:border-[#2EB4AC]/30 hover:bg-[#2EB4AC]/5 transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 group-hover:bg-[#2EB4AC]/10 flex items-center justify-center transition-colors">
                    <List className="h-5 w-5 text-slate-600 group-hover:text-[#2EB4AC]" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Task Log</p>
                    <p className="text-sm text-slate-500">View all tasks</p>
                  </div>
                </div>
              </div>
            </Link>

            <Link to="/settings" className="block">
              <div className="group p-4 rounded-xl border border-slate-200 hover:border-[#2EB4AC]/30 hover:bg-[#2EB4AC]/5 transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 group-hover:bg-[#2EB4AC]/10 flex items-center justify-center transition-colors">
                    <Settings className="h-5 w-5 text-slate-600 group-hover:text-[#2EB4AC]" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Settings</p>
                    <p className="text-sm text-slate-500">Manage masters</p>
                  </div>
                </div>
              </div>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
